import { AUTO_RESOLUTION } from '../../shared/media/mediaResolution';
import { MEDIA_KINDS, normalizeMediaSource } from '../media/mediaSource';
import { getPropAtPath } from './puckContentPath';
import { setBlockPropAtPath } from './siteBlocks';
import { variableImageBindings, variableImageProp } from './variableNodes';

/**
 * Emplacement d'image : un objet (les props d'un bloc, ou un objet média posé
 * dedans) qui porte une URL `X`, et à côté :
 * - `XVariable`, l'image du CRM qui la remplit (`variableImageProp`) ;
 * - `XForm`, ses réglages de formulaire `{ showInForm, name, remark,
 *   predefinedValues }`.
 *
 * Toute image du site suit cette convention, où qu'elle soit (fond de Bloc,
 * slide, carte, cellule) : le formulaire, le détecteur et l'éditeur ne
 * connaissent pas les noms de props des blocs.
 *
 * @typedef {{ path: string, urlProp: string, shape: 'flat' | 'media' }} ImageSlot
 *   `path` mène à l'objet (format `setDeep`, `''` pour les props du bloc).
 */

const FORM_SUFFIX = 'Form';

export const imageFormProp = (urlProp) => `${urlProp}${FORM_SUFFIX}`;

const MEDIA_KIND_VALUES = Object.values(MEDIA_KINDS);

// Un objet média (`normalizeMediaSource`) range sa méta sans préfixe.
const isMediaObject = (object) => MEDIA_KIND_VALUES.includes(object?.kind);

const formUrlProps = (object) => Object.entries(object)
  .filter(([prop, form]) => prop.endsWith(FORM_SUFFIX) && prop !== FORM_SUFFIX
    && form && typeof form === 'object' && !Array.isArray(form))
  .map(([prop]) => prop.slice(0, -FORM_SUFFIX.length));

const slotUrlProps = (object) => [...new Set([
  ...variableImageBindings(object).map(({ urlProp }) => urlProp),
  ...formUrlProps(object),
])];

const childPath = (path, key) => (path ? `${path}.${key}` : key);

/**
 * Les emplacements d'image des props d'un bloc : chaque objet qui porte une
 * variable image ou des réglages de formulaire, à n'importe quelle profondeur.
 *
 * @returns {ImageSlot[]} dans l'ordre des props
 */
export function collectImageSlots(props) {
  const slots = [];
  const walk = (value, path) => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => walk(item, `${path}[${index}]`));
      return;
    }
    if (!value || typeof value !== 'object') return;
    const shape = isMediaObject(value) ? 'media' : 'flat';
    slotUrlProps(value).forEach((urlProp) => slots.push({ path, urlProp, shape }));
    Object.entries(value).forEach(([key, child]) => {
      if (child && typeof child === 'object') walk(child, childPath(path, key));
    });
  };
  walk(props, '');
  return slots;
}

export const sameSlot = (a, b) => a.path === b.path && a.urlProp === b.urlProp && a.downloadId === b.downloadId;

export const slotObject = (props, { path }) => (
  path ? getPropAtPath(props, null, path) : props
) || {};

export const slotUrl = (props, slot) => slotObject(props, slot)[slot.urlProp] || '';

export const slotForm = (props, slot) => slotObject(props, slot)[imageFormProp(slot.urlProp)] || {};

export const slotBinding = (props, slot) => slotObject(props, slot)[variableImageProp(slot.urlProp)] || null;

/** Le chemin de l'URL elle-même (`slides[0].backgroundMedia.src`). */
export const slotPropPath = (slot) => `${childPath(slot.path, slot.urlProp)}${slot.downloadId ? `:${slot.downloadId}` : ''}`;

/**
 * Image qui attend sa valeur : une variable du CRM encore vide (modèle, champ
 * vide), ou une box que le formulaire remplira. L'éditeur montre l'image
 * d'attente à sa place.
 */
export const isSlotAwaitingImage = (props, slot) => !slotUrl(props, slot)
  && (!!slotBinding(props, slot)?.variableKey || slotForm(props, slot).showInForm === true);

/**
 * L'objet de l'emplacement réécrit par `update`.
 *
 * @returns {object} les **mêmes props** si `update` rend l'objet tel quel
 */
export function updateSlot(props, slot, update) {
  const current = slotObject(props, slot);
  const next = update(current);
  if (next === current) return props;
  return slot.path ? setBlockPropAtPath(props, slot.path, next) : next;
}

const flatMetaKeys = (name) => ({
  mediaId: `${name}MediaId`,
  resolution: `${name}Resolution`,
  srcset: `${name}Srcset`,
  variable: variableImageProp(name),
});

/** Ce que les props voisines disent du média de la prop `name`. */
export function mediaMetaFromProps(props, name) {
  const keys = flatMetaKeys(name);
  return {
    mediaId: props[keys.mediaId] ?? null,
    resolution: props[keys.resolution] || AUTO_RESOLUTION,
    srcset: props[keys.srcset] || '',
    variable: props[keys.variable] || null,
  };
}

/** Les props à écrire, en une fois, pour le média choisi. */
export function mediaMetaPatch(name, next) {
  const keys = flatMetaKeys(name);
  return {
    [name]: next.url,
    [keys.mediaId]: next.mediaId ?? null,
    [keys.resolution]: next.resolution || AUTO_RESOLUTION,
    [keys.srcset]: next.srcset || '',
    [keys.variable]: next.variable || null,
  };
}

/** L'image d'un objet média, vue comme un emplacement à la racine. */
export const MEDIA_OBJECT_SLOT = { path: '', urlProp: 'src', shape: 'media' };

/** La méta de l'image d'un emplacement, au format de `MediaUploadField`. */
export function slotMeta(props, slot) {
  const object = slotObject(props, slot);
  if (slot.shape === 'flat') return mediaMetaFromProps(object, slot.urlProp);
  return {
    mediaId: object.mediaId ?? null,
    resolution: object.resolution || AUTO_RESOLUTION,
    srcset: object.srcset || '',
    variable: object[variableImageProp('src')] || null,
  };
}

/**
 * Pose le média choisi (`{ url, mediaId, resolution, srcset, variable }`,
 * l'écriture de `MediaUploadField`) dans l'objet de l'emplacement. Sans
 * `variable`, l'image est détachée du CRM.
 */
export function withSlotMedia(object, slot, next) {
  if (slot.shape === 'flat') return { ...object, ...mediaMetaPatch(slot.urlProp, next) };
  return normalizeMediaSource({
    ...object,
    kind: MEDIA_KINDS.image,
    src: next.url,
    mediaId: next.mediaId ?? null,
    resolution: next.resolution || AUTO_RESOLUTION,
    srcset: next.srcset || '',
    [variableImageProp('src')]: next.variable || null,
  });
}
